import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { parkingService } from '../services/api';
import type { ParkingState, ParkingSpot, ParkingSpotType } from '../types';

// Initial state
const initialState: ParkingState = {
  spots: [],
  loading: false,
  error: null,
};

// Async thunks for parking
export const fetchParkingSpots = createAsyncThunk(
  'parking/fetchParkingSpots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await parkingService.getAll();
      return response.data.data as ParkingSpot[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch parking spots');
    }
  }
);

export const createParkingSpot = createAsyncThunk(
  'parking/createParkingSpot',
  async (spotData: { spot_number: string; location?: string; spot_type: ParkingSpotType }, { rejectWithValue }) => {
    try {
      const response = await parkingService.create(spotData);
      return response.data.data as ParkingSpot;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create parking spot');
    }
  }
);

export const updateParkingSpot = createAsyncThunk(
  'parking/updateParkingSpot',
  async ({ id, data }: { id: number; data: Partial<ParkingSpot> }, { rejectWithValue }) => {
    try {
      const response = await parkingService.update(id, data);
      return response.data.data as ParkingSpot;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update parking spot');
    }
  }
);

export const assignVehicleToSpot = createAsyncThunk(
  'parking/assignVehicleToSpot',
  async ({ spotId, vehicleId }: { spotId: number; vehicleId: number }, { rejectWithValue }) => {
    try {
      const response = await parkingService.assignVehicle(spotId, vehicleId);
      return { spotId, vehicleId, spotData: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign vehicle to parking spot');
    }
  }
);

export const releaseVehicleFromSpot = createAsyncThunk(
  'parking/releaseVehicleFromSpot',
  async (spotId: number, { rejectWithValue }) => {
    try {
      const response = await parkingService.releaseVehicle(spotId);
      return { spotId, spotData: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to release vehicle from parking spot');
    }
  }
);

// Parking slice
const parkingSlice = createSlice({
  name: 'parking',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateSpotOccupancy: (state, action: PayloadAction<{ spotId: number; isOccupied: boolean; vehicleId?: number }>) => {
      const spot = state.spots.find(s => s.id === action.payload.spotId);
      if (spot) {
        spot.is_occupied = action.payload.isOccupied;
        // Note: In a real app, you'd also update the vehicle reference
      }
    },
    addParkingSpot: (state, action: PayloadAction<ParkingSpot>) => {
      state.spots.push(action.payload);
    },
    removeParkingSpot: (state, action: PayloadAction<number>) => {
      state.spots = state.spots.filter(spot => spot.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch parking spots
    builder
      .addCase(fetchParkingSpots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParkingSpots.fulfilled, (state, action) => {
        state.loading = false;
        state.spots = action.payload;
      })
      .addCase(fetchParkingSpots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Create parking spot
    builder
      .addCase(createParkingSpot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createParkingSpot.fulfilled, (state, action) => {
        state.loading = false;
        state.spots.push(action.payload);
      })
      .addCase(createParkingSpot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Update parking spot
    builder
      .addCase(updateParkingSpot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateParkingSpot.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.spots.findIndex(spot => spot.id === action.payload.id);
        if (index !== -1) {
          state.spots[index] = action.payload;
        }
      })
      .addCase(updateParkingSpot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Assign vehicle to spot
    builder
      .addCase(assignVehicleToSpot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignVehicleToSpot.fulfilled, (state, action) => {
        state.loading = false;
        const spot = state.spots.find(s => s.id === action.payload.spotId);
        if (spot) {
          spot.is_occupied = true;
          // Update with the latest spot data from server
          Object.assign(spot, action.payload.spotData);
        }
      })
      .addCase(assignVehicleToSpot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Release vehicle from spot
    builder
      .addCase(releaseVehicleFromSpot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(releaseVehicleFromSpot.fulfilled, (state, action) => {
        state.loading = false;
        const spot = state.spots.find(s => s.id === action.payload.spotId);
        if (spot) {
          spot.is_occupied = false;
          // Update with the latest spot data from server
          Object.assign(spot, action.payload.spotData);
        }
      })
      .addCase(releaseVehicleFromSpot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  updateSpotOccupancy,
  addParkingSpot,
  removeParkingSpot,
} = parkingSlice.actions;

export default parkingSlice.reducer; 